import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

export const MAILERLITE_ACTION =
  "https://assets.mailerlite.com/jsonp/2170178/forms/181313002749822272/subscribe";

export default function NewsletterSignupForm({
  prompt,
  submitLabel = "Subscribe",
  idleMessage = "Press enter or submit to join the list.",
  sentMessage = "Request sent. If you receive a confirmation email, follow its link to finish signing up.",
  onSubmit,
  autoFocus = false,
  showCloseOnSent = false,
  onClose,
  inputId = "newsletter-email",
}) {
  const inputRef = useRef(null);
  const submittedRef = useRef(false);
  const confirmationTimerRef = useRef(null);
  const iframeName = `mailerlite-newsletter-frame-${useId()}`;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const isSent = status === "sent";
  const isPending = status === "submitted";

  useEffect(() => {
    if (autoFocus && !isSent) {
      inputRef.current?.focus();
    }
  }, [autoFocus, isSent]);

  useEffect(() => () => window.clearTimeout(confirmationTimerRef.current), []);

  function handleFrameLoad() {
    if (!submittedRef.current) {
      return;
    }

    window.clearTimeout(confirmationTimerRef.current);
    submittedRef.current = false;
    setStatus("sent");
    setMessage(sentMessage);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (submittedRef.current) return;

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setStatus("error");
      setMessage("Enter an email address.");
      inputRef.current?.focus();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus("error");
      setMessage("Enter a valid email address.");
      inputRef.current?.focus();
      return;
    }

    setStatus("submitted");
    setMessage("Submitting...");
    submittedRef.current = true;
    onSubmit?.(trimmedEmail);

    event.currentTarget.submit();

    confirmationTimerRef.current = window.setTimeout(() => {
      submittedRef.current = false;
      setStatus("error");
      setMessage("We could not confirm that request. Please try again.");
    }, 12000);
  }

  return (
    <div className="newsletter-signup">
      {prompt ? <div className="newsletter-overlay__prompt">{prompt}</div> : null}
      <form className={`newsletter-form ${isSent ? "newsletter-form--sent" : ""}`} action={MAILERLITE_ACTION} method="post" target={iframeName} onSubmit={handleSubmit} noValidate>
        {!isSent && (
          <>
            <div className="newsletter-form__field">
              <label className="sr-only" htmlFor={inputId}>Email</label>
              <input
                id={inputId}
                ref={inputRef}
                type="email"
                name="fields[email]"
                className="newsletter-form__input"
                placeholder="Email"
                autoComplete="email"
                value={email}
                readOnly={isPending}
                onChange={(event) => setEmail(event.target.value)}
                aria-describedby={`${inputId}-status`}
                aria-invalid={status === "error"}
              />
            </div>
            <input type="hidden" name="ml-submit" value="1" />
            <input type="hidden" name="anticsrf" value="true" />
            <div className="newsletter-form__actions">
              <button className="newsletter-form__submit" type="submit" disabled={isPending}>
                {isPending ? "Sending…" : submitLabel}
              </button>
            </div>
          </>
        )}
        <p id={`${inputId}-status`} className={`newsletter-form__status is-${status}`} role="status" aria-live="polite" aria-atomic="true">
          {message || idleMessage}
        </p>
        {isSent && (
          <div className="newsletter-form__actions">
            <button className="newsletter-form__submit" type="button" onClick={() => {
              setStatus("idle");
              setMessage("");
              window.requestAnimationFrame(() => inputRef.current?.focus());
            }}>No email? Try again</button>
            {showCloseOnSent && onClose && <button className="newsletter-form__submit" type="button" onClick={onClose}>Close</button>}
          </div>
        )}
      </form>
      <p className="newsletter-form__privacy">
        Book and author updates from R A Hewitt. Unsubscribe at any time. <Link to="/privacy">How your email is used</Link>.
      </p>
      <iframe
        title="MailerLite subscription target"
        name={iframeName}
        className="newsletter-form__frame"
        aria-hidden="true"
        tabIndex="-1"
        onLoad={handleFrameLoad}
      />
    </div>
  );
}
